import React from 'react';
import { motion } from 'framer-motion';
import useOutsideAlerter from '../hooks/useOutsideAlerter';
import { useChat } from '../modules/ChatPane';
import JUNOSelect from './JUNOSelect';

export default function ChatSubmit({ action, room }) {
  const {
    context: { content, isModerator, pollActive },
  } = useChat();
  const {
    data: { channeltype },
  } = action;

  // different controlled forms
  const [textInput, setTextInput] = React.useState('');
  const [pollQuestion, setPollQuestion] = React.useState('');
  const [pollTimer, setPollTimer] = React.useState(30);
  const [anonymousSubmit, setAnonymousSubmit] = React.useState(false);

  // refs for emoji injection
  const emojiRef = React.useRef(null);
  const emojiRefContainer = React.useRef(null);
  const emojiOutsideAlerterRef = React.useRef(null);

  // other vars
  const enableAnonymous = content.GetActionById(CrowdHub_App.ACTION_ANONYMOUS_QUESTION);

  // handlers
  const handleTextInput = e => setTextInput(e.target.value);
  const handleTextFocus = e => {};
  const handleTextBlur = e => {};

  const handleAnonymousInput = e => setAnonymousSubmit(old => !old);
  const handlePollQuestionChange = value => setPollQuestion(value);
  const handlePollTimerInput = e => setPollTimer(e.target.value);

  const onSubmitChat = e => {
    e.preventDefault();
    // don't allow if there isn't a real character
    if (!textInput.replace(/\s/g, '').length) {
      return;
    }
    PubNubCenter.INSTANCE.messageChannel(room, textInput, anonymousSubmit, (status, response) => {
      if (status.statusCode === 404) {
        // no history.
        return;
      } else if (status.error) {
        //TODO: handle error better
        console.error(status.error);
        return;
      } else {
        setTextInput('');
        reportAnalytics();
      }
    });
    if (channeltype === OTSessionContent.CHANNELTYPE_QA && action.data.moderateFlow) {
      let toastr = new ToastrMessages({}, CrowdHub_App.LANG.QA_QUESTION_SUBMITTED, 'info', {
        positionClass: 'toast-bottom-right toast-align-with-chat-pane',
        timeOut: 7000,
        extendedTimeOut: 3000,
      });
      toastr.showToastr();
    }
    const liveChatAction = content.GetActionById(CrowdHub_App.ACTION_LIVECHAT);
    const qaAction = content.GetActionById(CrowdHub_App.ACTION_QA);

    switch (channeltype) {
      case OTSessionContent.CHANNELTYPE_QA:
        if (qaAction) {
          qaAction.Complete(content.Id, () => {}, null, false);
        }
        break;
      case OTSessionContent.CHANNELTYPE_CHAT:
        if (liveChatAction) {
          liveChatAction.Complete(content.Id, () => {}, null, false);
        }
        break;
      default:
        break;
    }
  };

  const onSubmitPoll = e => {
    e.preventDefault();
    if (pollActive) {
      return;
    }
    let vars = {
      question: pollQuestion,
      id: `${App.INSTANCE.CurrentUser.Id}_${moment().utc().valueOf()}`,
      time: pollTimer,
      modType: 'pollposted',
    };

    //TODO: check if question is active
    //TODO: check if question is too many bytes
    PubNubCenter.INSTANCE.modMessageChannel(room, vars);
  };

  const parseCategory = (channel: string) => {
    // Handle case where channel is empty or undefined
    if (!channel || !channel.length) {
      return 'Unknown';
    }

    // If channel is a single character, return it as uppercase
    if (channel.length === 1) {
      return channel.toUpperCase();
    }

    // Channel of qa should be returned as 'Q&A'
    if (channel === 'qa') {
      return channel
        .split('')
        .map(function (letter) {
          return letter.toUpperCase();
        })
        .join('&');
    }

    // Capitalize the first character
    return channel[0].toUpperCase() + channel.slice(1);
  };

  const reportAnalytics = () => {
    switch (channeltype) {
      case OTSessionContent.CHANNELTYPE_QA:
        App.INSTANCE.reportActivity(content.Id, 'QA', parseCategory(channeltype), textInput);

        App.INSTANCE.firehoseActivity(
          ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_CONTENT,
          content.Id,
          App.INSTANCE.appRouter.lastRouteResolvedUrl(),
          App.INSTANCE.CurrentUser.Id,
          content.FirstBucketName(),
          ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_QA.value,
          textInput,
          null,
        );
        break;
      case OTSessionContent.CHANNELTYPE_MOD:
        App.INSTANCE.reportActivity(content.Id, 'Mod', parseCategory(channeltype), textInput);

        App.INSTANCE.firehoseActivity(
          ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_CONTENT,
          content.Id,
          App.INSTANCE.appRouter.lastRouteResolvedUrl(),
          App.INSTANCE.CurrentUser.Id,
          content.FirstBucketName(),
          ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_MOD.value,
          textInput,
          null,
        );
        break;
      case OTSessionContent.CHANNELTYPE_CHAT:
        App.INSTANCE.reportActivity(content.Id, 'Chat', parseCategory(channeltype), textInput);

        App.INSTANCE.firehoseActivity(
          ANALYTICS_CONFIGURATION.FIREHOSE_OBJECTS.OBJECT_CONTENT,
          content.Id,
          App.INSTANCE.appRouter.lastRouteResolvedUrl(),
          App.INSTANCE.CurrentUser.Id,
          content.FirstBucketName(),
          ANALYTICS_CONFIGURATION.FIREHOSE_ACTIONS.ACTION_CHAT.value,
          textInput,
          null,
        );
        break;
      case OTSessionContent.CHANNELTYPE_COHORT:
      default:
        break;
    }
  };

  useOutsideAlerter(emojiOutsideAlerterRef, e => {
    e.stopPropagation();
    if (e.target == emojiRef.current.pickerEl) {
    } else {
      emojiRef.current.hidePicker();
    }
  });

  // create the emojiPicker. kind of ghetto to use refs like this but the
  // react-emojipicker sucked compared to this one
  React.useEffect(() => {
    setTextInput('');

    let emojiArray = ['smileys', 'people', 'animals', 'food', 'activities', 'travel', 'objects', 'symbols'];

    emojiRef.current = new EmojiButton({
      autoHide: false,
      emojisPerRow: 6,
      emojiSize: '22px',
      initialCategory: 'recents',
      categories: emojiArray,
      i18n: {
        search: 'Search',
        categories: {
          recents: 'Recently Used',
          smileys: 'Smileys & People',
          animals: 'Animals & Nature',
          food: 'Food & Drink',
          activities: 'Activities',
          travel: 'Travel & Places',
          objects: 'Objects',
          symbols: 'Symbols',
          flags: 'Flags',
        },
        notFound: 'No emojis found',
      },
      position: {
        bottom: '103%',
        left: '0px',
        top: 'auto',
      },
      recentsCount: 10,
      rootElement: emojiRefContainer.current,
      rows: Environment.IS_MOBILEDEVICE ? 2 : 4,
      showCategoryButtons: false,
      showPreview: false,
      showRecents: true,
      showSearch: true,
      theme: 'dark',
    });

    // Excluding flags from category list works for searched emojis, but the all emojis list still includes flags, so delete them here
    let flagsLabel = $(emojiRef.current.pickerContent)
      .find('.emoji-picker__category-name')
      .filter(":contains('Flags')");
    let flagsCategory = flagsLabel.next();

    flagsLabel.remove();
    flagsCategory.remove();

    emojiRef.current.on('emoji', selection => {
      setTextInput(old => old + selection.emoji);
    });

    $(emojiRefContainer.current).on('click', e => {
      e.stopPropagation();
    });

    return () => {
      emojiRef.current.destroyPicker();
    };
  }, []);

  let formJSX;

  // big 'ol switch for our form JSX
  switch (channeltype) {
    case OTSessionContent.CHANNELTYPE_POLL:
      formJSX = (
        <form onSubmit={onSubmitPoll}>
          <div className={`form_horizontal`}>
            <JUNOSelect
              options={content.Questions.map(question => {
                return {
                  value: question.Id,
                  name: question.Name,
                };
              })}
              onChange={handlePollQuestionChange}
              placeHolder={`Please select a question`}
              dropUp={true}
              theme={`dark`}
            />
          </div>
          <div className={`form_horizontal text_input`}>
            <i className={`fa-duotone fa-timer timer_icon`}></i>
            <input value={pollTimer} onChange={handlePollTimerInput} type={`number`} />
            <button
              type={`submit`}
              className={pollQuestion !== '' && !pollActive ? 'highlight submit_button' : 'submit_button'}
              style={
                pollQuestion !== '' && !pollActive
                  ? {}
                  : {
                      cursor: 'default',
                    }
              }
            >
              <i className={`fa fad fa-send`} />
              <div className={`tooltip`}>
                {pollActive
                  ? CrowdHub_App.LANG.POLL_IS_ACTIVE_TOOLTIP
                  : CrowdHub_App.LANG.POLL_SELECT_A_QUESTION_TOOLTIP}
              </div>
            </button>
          </div>
        </form>
      );
      break;

    case OTSessionContent.CHANNELTYPE_QA:
    case OTSessionContent.CHANNELTYPE_CHAT:
    case OTSessionContent.CHANNELTYPE_MOD:
      formJSX = (
        <>
          <div className={`form_horizontal`} style={{ justifyContent: 'flex-end' }}>
            {enableAnonymous && channeltype != OTSessionContent.CHANNELTYPE_MOD && (
              <div className={'anonymous'} onClick={handleAnonymousInput}>
                <Switch isOn={anonymousSubmit} />

                {CrowdHub_App.LANG.SEND_ANONYMOUSLY}
              </div>
            )}
          </div>
          <form onSubmit={onSubmitChat}>
            <div className={`form_horizontal text_input`}>
              <input
                className="exclude_outline"
                placeholder={`Type here`}
                onChange={handleTextInput}
                value={textInput}
                onFocus={handleTextFocus}
                onBlur={handleTextBlur}
              />
              <button
                type={`submit`}
                className={textInput.replace(/\s/g, '').length ? 'highlight submit_button' : 'submit_button'}
                style={textInput.replace(/\s/g, '').length ? {} : { cursor: 'default' }}
              >
                <i className={`fa fad fa-send`} />
              </button>
            </div>
          </form>
        </>
      );
      break;

    default:
      formJSX = <></>;
  }

  return (
    <div>
      {(channeltype == OTSessionContent.CHANNELTYPE_POLL && !isModerator) ||
      channeltype == OTSessionContent.CHANNELTYPE_UPDATE ? (
        <></>
      ) : (
        formJSX
      )}
      <button
        className={`emoji_container_button`}
        aria-label="emoji picker"
        tabIndex={0}
        onClick={e => {
          if (emojiRef.current.isPickerVisible()) {
            emojiRef.current.hidePicker();
          } else {
            emojiRef.current.showPicker();
          }
        }}
        ref={emojiOutsideAlerterRef}
        style={{ border: 'none', background: 'none' }}
      >
        <i className={`fas fa-smile-plus chat_submit__emoji_icon`}></i>
        <div ref={emojiRefContainer} />
      </button>
    </div>
  );
}

function Switch({ isOn, ...props }) {
  const className = `switch ${isOn ? 'on' : 'off'}`;

  return (
    <div className={className} {...props}>
      <motion.div layout transition={spring} />
    </div>
  );
}

const spring = {
  type: 'spring',
  stiffness: 700,
  damping: 30,
};
